import React, { useState, useEffect } from "react";
import "./dstyle.css";
import SideBar from "./SideBar";
import Navbar from "./Navbar";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserShield,
  faPlus,
  faTrash,
  faKey,
  faEnvelope,
  faSpinner,
  faShieldAlt,
  faCrown,
  faUserCog
} from "@fortawesome/free-solid-svg-icons";
import { Modal } from "antd";
import { toast } from "react-toastify";
import { API_BASE_URL } from "../../config/api";

function DAdmins() {
  const navigate = useNavigate();
  const adminRole = localStorage.getItem("adminRole");
  const adminId = localStorage.getItem("adminId");

  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedAdminId, setSelectedAdminId] = useState(null);
  const [newPassword, setNewPassword] = useState("");

  const [createForm, setCreateForm] = useState({ username: "", email: "", password: "" });

  useEffect(() => {
    if (!adminId) { navigate("/admin-login"); return; }
    if (adminRole !== "main") { navigate("/dashboard"); return; }
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admins`);
      const data = await res.json();
      setAdmins(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createForm.username || !createForm.email || !createForm.password) {
      toast.error("All fields are required."); return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/admins/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...createForm, createdBy: adminId })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success("Sub-admin account created!");
        setShowCreateModal(false);
        setCreateForm({ username: "", email: "", password: "" });
        fetchAdmins();
      } else {
        toast.error(data.error || "Failed to create sub-admin.");
      }
    } catch (err) {
      toast.error("Server error.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this sub-admin account?")) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/admins/${id}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Sub-admin deleted.");
        fetchAdmins();
      } else {
        const data = await res.json();
        toast.error(data.error || "Cannot delete this account.");
      }
    } catch (err) {
      toast.error("Server error.");
    }
  };

  const handlePasswordReset = async () => {
    if (!newPassword || newPassword.length < 4) {
      toast.error("Password must be at least 4 characters."); return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/admins/${selectedAdminId}/password`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: newPassword })
      });
      if (res.ok) {
        toast.success("Password updated.");
        setShowPasswordModal(false);
        setNewPassword("");
      } else {
        toast.error("Failed to update password.");
      }
    } catch (err) {
      toast.error("Server error.");
    }
  };

  return (
    <div style={{ backgroundColor: "#f8fafc", minHeight: "100vh" }}>
      <SideBar current={"admins"} />
      <section id="content">
        <Navbar />
        <main style={{ padding: "28px" }}>

          {/* Header */}
          <div style={{ marginBottom: "28px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px" }}>
            <div>
              <h1 style={{ color: "#0f172a", fontFamily: "'Outfit', sans-serif", fontSize: "1.8rem", fontWeight: 800, margin: 0 }}>
                Admin Account Management
              </h1>
              <p style={{ color: "#64748b", margin: "4px 0 0", fontSize: "0.9rem" }}>
                Create and manage sub-admin accounts with limited dashboard access
              </p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              style={{
                background: "linear-gradient(135deg, #4f46e5 0%, #3730a3 100%)",
                color: "#fff", border: "none", borderRadius: "10px",
                padding: "10px 20px", fontWeight: 700, fontSize: "0.9rem",
                cursor: "pointer", display: "flex", alignItems: "center", gap: "8px",
                boxShadow: "0 4px 14px rgba(79,70,229,0.3)"
              }}
            >
              <FontAwesomeIcon icon={faPlus} /> Create Sub-Admin
            </button>
          </div>

          {/* Admin Accounts Table */}
          <div style={{ background: "#fff", borderRadius: "20px", border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.03)" }}>
            <div style={{ padding: "18px 24px", borderBottom: "1px solid #f1f5f9", background: "#fafbff", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <h3 style={{ margin: 0, fontWeight: 800, color: "#0f172a", fontSize: "1.05rem", display: "flex", alignItems: "center", gap: "8px" }}>
                <FontAwesomeIcon icon={faUserShield} style={{ color: "#4f46e5" }} />
                All Administrator Accounts
              </h3>
              <span style={{ background: "#e0e7ff", color: "#4f46e5", borderRadius: "20px", padding: "4px 14px", fontSize: "0.82rem", fontWeight: 700 }}>
                {admins.length} Accounts
              </span>
            </div>

            <div style={{ overflowX: "auto" }}>
              {loading ? (
                <div style={{ textAlign: "center", padding: "40px 0", color: "#64748b" }}>
                  <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: "1.8rem", color: "#4f46e5", marginBottom: "10px" }} />
                  <p>Loading admin accounts...</p>
                </div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: "0.9rem" }}>
                  <thead>
                    <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0", color: "#64748b", fontWeight: "700" }}>
                      <th style={{ padding: "14px 24px" }}>Admin Name</th>
                      <th style={{ padding: "14px 24px" }}>Email</th>
                      <th style={{ padding: "14px 24px", textAlign: "center" }}>Role</th>
                      <th style={{ padding: "14px 24px", textAlign: "center" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {admins.map((a) => (
                      <tr key={a.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                        <td style={{ padding: "16px 24px", fontWeight: "700", color: "#0f172a" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <div style={{
                              width: "36px", height: "36px", borderRadius: "50%",
                              background: a.role === "main" ? "#fef3c7" : "#e0e7ff",
                              color: a.role === "main" ? "#d97706" : "#4f46e5",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              fontWeight: 800, fontSize: "0.85rem"
                            }}>
                              <FontAwesomeIcon icon={a.role === "main" ? faCrown : faUserCog} />
                            </div>
                            {a.username}
                          </div>
                        </td>
                        <td style={{ padding: "16px 24px", color: "#475569" }}>
                          <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: "6px", color: "#818cf8" }} />
                          {a.email}
                        </td>
                        <td style={{ padding: "16px 24px", textAlign: "center" }}>
                          <span style={{
                            background: a.role === "main" ? "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)" : "#e0e7ff",
                            color: a.role === "main" ? "#fff" : "#4f46e5",
                            padding: "4px 14px", borderRadius: "20px", fontSize: "0.8rem", fontWeight: 700
                          }}>
                            {a.role === "main" ? "Main Admin" : "Sub-Admin"}
                          </span>
                        </td>
                        <td style={{ padding: "16px 24px", textAlign: "center" }}>
                          {a.role !== "main" ? (
                            <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                              <button
                                onClick={() => { setSelectedAdminId(a.id); setShowPasswordModal(true); }}
                                style={{ background: "#e0e7ff", color: "#4f46e5", border: "none", borderRadius: "8px", padding: "6px 12px", cursor: "pointer", fontWeight: 700, fontSize: "0.82rem" }}
                                title="Reset Password"
                              >
                                <FontAwesomeIcon icon={faKey} />
                              </button>
                              <button
                                onClick={() => handleDelete(a.id)}
                                style={{ background: "#fee2e2", color: "#ef4444", border: "none", borderRadius: "8px", padding: "6px 12px", cursor: "pointer", fontWeight: 700, fontSize: "0.82rem" }}
                                title="Delete Account"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </div>
                          ) : (
                            <span style={{ color: "#94a3b8", fontSize: "0.82rem", fontWeight: 600 }}>Protected</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </main>
      </section>

      {/* Create Sub-Admin Modal */}
      <Modal
        title="Create New Sub-Admin Account"
        open={showCreateModal}
        onCancel={() => setShowCreateModal(false)}
        footer={null}
      >
        <form onSubmit={handleCreate} style={{ display: "flex", flexDirection: "column", gap: "16px", paddingTop: "10px" }}>
          <div>
            <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#334155", display: "block", marginBottom: "6px" }}>Full Name</label>
            <input type="text" value={createForm.username} onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })}
              style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "1.5px solid #cbd5e1", boxSizing: "border-box" }} required />
          </div>
          <div>
            <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#334155", display: "block", marginBottom: "6px" }}>Email Address</label>
            <input type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
              style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "1.5px solid #cbd5e1", boxSizing: "border-box" }} required />
          </div>
          <div>
            <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#334155", display: "block", marginBottom: "6px" }}>Password</label>
            <input type="password" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
              style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "1.5px solid #cbd5e1", boxSizing: "border-box" }} required />
          </div>
          <p style={{ color: "#64748b", fontSize: "0.82rem", margin: 0 }}>
            Sub-admins can only add courses and manage quizzes. They cannot access user data or admin management.
          </p>
          <button type="submit" style={{
            background: "linear-gradient(135deg, #4f46e5 0%, #3730a3 100%)", color: "#fff", border: "none",
            padding: "12px", borderRadius: "10px", fontWeight: 700, cursor: "pointer", marginTop: "4px"
          }}>
            Create Sub-Admin Account
          </button>
        </form>
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        title="Reset Sub-Admin Password"
        open={showPasswordModal}
        onOk={handlePasswordReset}
        onCancel={() => { setShowPasswordModal(false); setNewPassword(""); }}
        okText="Update Password"
      >
        <div style={{ paddingTop: "10px" }}>
          <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#334155", display: "block", marginBottom: "8px" }}>New Password</label>
          <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Enter new password (min 4 chars)"
            style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "1.5px solid #cbd5e1", boxSizing: "border-box" }} />
        </div>
      </Modal>
    </div>
  );
}

export default DAdmins;
